// Types
import type { TOutboxEmail } from '~/modules/opportunities/automated-email/types';
import type { TOutboxRowViewModel } from '~/modules/opportunities/automated-email/outbox/types';

// Config
import { LISTING_STATUS_ID_TO_EVENT_LABEL } from '~/modules/opportunities/automated-email/outbox/config';

// Utils
import { formatPrice } from '~/misc/utils/number';

const CELL_PART_SEPARATOR: string = ' · ';

function getBedsLabel(beds: number): string {
  return `${beds} Bed${beds === 1 ? '' : 's'}`;
}

/** "Beds + Type + est Value", e.g. "3 Beds · Terraced · est. £391,000". */
export function getRecipientPropertySummary(email: TOutboxEmail): string {
  const parts: string[] = [];

  if (typeof email.opportunity_property_beds === 'number') {
    parts.push(getBedsLabel(email.opportunity_property_beds));
  }

  if (email.opportunity_property_category) {
    parts.push(email.opportunity_property_category);
  }

  if (typeof email.opportunity_property_price === 'number') {
    parts.push(`est. ${formatPrice(email.opportunity_property_price)}`);
  }

  return parts.length > 0 ? parts.join(CELL_PART_SEPARATOR) : '-';
}

export function getListingEventLabel(listingStatusId: number | null | undefined): string | null {
  if (typeof listingStatusId !== 'number') {
    return null;
  }

  return LISTING_STATUS_ID_TO_EVENT_LABEL[listingStatusId] ?? null;
}

/** "Beds + Type + Event + Price", e.g. "3 Beds · House · SSTC · £380,000". */
export function getListingSummary(email: TOutboxEmail): string {
  const parts: string[] = [];

  if (typeof email.listing_beds === 'number') {
    parts.push(getBedsLabel(email.listing_beds));
  }

  if (email.listing_category) {
    parts.push(email.listing_category);
  }

  const eventLabel: string | null = getListingEventLabel(email.listing_status_id);

  if (eventLabel) {
    parts.push(eventLabel);
  }

  if (typeof email.listing_price === 'number') {
    parts.push(formatPrice(email.listing_price));
  }

  return parts.length > 0 ? parts.join(CELL_PART_SEPARATOR) : '-';
}

/** Strips HTML tags from email content for a single-line plain text excerpt. */
export function getEmailContentExcerpt(content: string): string {
  return content
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function buildOutboxRowViewModel(email: TOutboxEmail): TOutboxRowViewModel {
  return {
    recipient: {
      name: email.contact_name ?? '',
      address: email.opportunity_property_address ?? '',
      propertySummary: getRecipientPropertySummary(email),
    },
    reason: {
      triggerLabel: email.trigger,
      address: email.listing_address ?? '',
      listingSummary: getListingSummary(email),
    },
    subject: {
      subject: "Subject: " + email.subject,
      preview: getEmailContentExcerpt(email.content),
    },
  };
}
